import { ensureArray } from '@examdojo/util/ensure-array';
import type { OrArray } from '@ngneat/elf';
import type { Board, GeometryElement } from 'jsxgraph';
import type { ActionDefinition, ActionResult } from '../../../model/widget-action.model';
import type { EventMap, WidgetEventSelectors } from '../../../model/widget-event.model';

export interface EventWiringOptions<ActionType extends string, ActionTarget extends string> {
  board: Board;
  objects: Map<string, GeometryElement>;
  selectors: WidgetEventSelectors<ActionTarget, ActionType>;
  executeAction: (action: ActionDefinition<ActionType>) => Promise<ActionResult>;
}

export function wireEvents<ActionType extends string, ActionTarget extends string>(
  opts: EventWiringOptions<ActionType, ActionTarget>,
): void {
  const { board, objects, selectors, executeAction } = opts;

  const executeEventActions = async (actions: OrArray<ActionDefinition<ActionType>>): Promise<void> => {
    const actionArray = ensureArray(actions);
    for (const action of actionArray) {
      await executeAction(action);
    }
  };

  Object.entries(selectors).forEach(([key, eventMap]) => {
    if (!eventMap) {
      return;
    }

    if (key === 'canvas') {
      wireCanvasEvents(board, eventMap as EventMap<ActionType>, executeEventActions);
    } else {
      const obj = objects.get(key);
      if (obj) {
        wireObjectEvents(obj, eventMap as EventMap<ActionType>, executeEventActions);
      }
    }
  });
}

function wireCanvasEvents<ActionType extends string>(
  board: Board,
  eventMap: EventMap<ActionType>,
  executeEventActions: (actions: OrArray<ActionDefinition<ActionType>>) => Promise<void>,
): void {
  if (eventMap.onClick) {
    board.on('down', () => executeEventActions(eventMap.onClick!));
  }
  if (eventMap.onScroll) {
    board.on('mousewheel', () => executeEventActions(eventMap.onScroll!));
  }
}

function wireObjectEvents<ActionType extends string>(
  obj: GeometryElement,
  eventMap: EventMap<ActionType>,
  executeEventActions: (actions: OrArray<ActionDefinition<ActionType>>) => Promise<void>,
): void {
  if (eventMap.onHover) {
    obj.on('over', () => executeEventActions(eventMap.onHover!));
  }
  if (eventMap.onHoverOut) {
    obj.on('out', () => executeEventActions(eventMap.onHoverOut!));
  }
  if (eventMap.onClick) {
    obj.on('down', () => executeEventActions(eventMap.onClick!));
  }
  if (eventMap.onDrag) {
    obj.on('drag', () => executeEventActions(eventMap.onDrag!));
  }
}
